import React, { useCallback, useEffect, useState } from "react";
import { StyleSheet, SectionList, View, ScrollView } from "react-native";
import { colors } from "src/assets";
import {
  BottomActions,
  Button,
  UserCard,
  CallDataRow,
  CallOffer,
  Typography,
  ReviewCard,
  MessageSection,
  buildMessagesSections,
} from "src/components";
import { useNavigation, useRoute } from "@react-navigation/native";
import {
  useCall,
  useCognito,
  useOffer,
  useUser,
  useCallActions,
  useChatMessages,
} from "src/hooks";
import { HistoryRow } from "./components";
import { stringDateFullDynamicFormat, stringTimeFormat } from "src/utils/date";
import { checkReviewExpected } from "src/utils/call";
import { LeftMessage, RightMessage } from "src/components/chat/components";
import LocalText from "src/local";
import alert from "src/components/alert";
import { RootStackScreenProps } from "src/navigation/types";

export type CallHistoryNavigationParam = {
  callId: string;
};

export const CallHistoryScreen: React.FC = () => {
  const navigation =
    useNavigation<RootStackScreenProps<"CallHistory">["navigation"]>();
  const route = useRoute<RootStackScreenProps<"CallHistory">["route"]>();
  const [cognitoUsername] = useCognito();
  const [call] = useCall(route.params.callId);
  const [offer] = useOffer(call?.serviceId);
  const [user] = useUser(
    call?.senderId === cognitoUsername ? call?.opponentId : call?.senderId
  );
  const { requestReofferCall } = useCallActions();
  const [messages] = useChatMessages(route.params.callId);
  const [sections, setSections] = useState<MessageSection[]>([]);
  useEffect(() => {
    setSections(buildMessagesSections(messages));
  }, [messages]);

  const reofferHandler = useCallback(() => {
    if (offer) {
      navigation.push("CallRequest", { offerId: offer.id });
    }
  }, [offer]);
  const requestReofferHandler = useCallback(async () => {
    if (call) {
      alert(LocalText.screens.callHistory.requestReofferAlert, undefined, [
        {
          text: LocalText.common.yes,
          onPress: async () => {
            await requestReofferCall({ id: call.id });
            alert(
              LocalText.screens.callHistory.requestOfferAlertSuccessTitle,
              LocalText.screens.callHistory.requestOfferAlertSuccessDescription
            );
          },
        },
        { text: LocalText.common.no },
      ]);
    }
  }, [call]);

  return (
    <View style={styles.container}>
      {call ? (
        <React.Fragment>
          <ScrollView
            style={styles.scrollView}
            showsVerticalScrollIndicator={false}
          >
            {user ? (
              <UserCard
                contanerStyle={styles.user}
                source={user?.image ? { uri: user?.image } : undefined}
                status={user?.status}
                name={user?.name}
                birthday={user?.birthday}
                gender={user?.gender}
                prefecture={user?.address?.prefecture}
                isVerified={user?.identificationStatus === "APPROVED"}
                onPress={() =>
                  navigation.push("User", { id: user.id, title: "" })
                }
              />
            ) : null}
            {offer ? (
              <React.Fragment>
                <Typography
                  text={LocalText.screens.callHistory.service}
                  variant={"section"}
                />
                <CallOffer offer={offer} />
                <CallDataRow
                  text={LocalText.screens.callHistory.price}
                  detail={`¥${call.price} / 1分`}
                />
              </React.Fragment>
            ) : null}
            <Typography
              text={LocalText.screens.callHistory.history}
              variant={"section"}
            />
            <HistoryRow
              date={stringDateFullDynamicFormat(call.createdAt)}
              time={stringTimeFormat(call.createdAt)}
              text={LocalText.screens.callHistory.requestStarted}
            />
            {["CANCELED_BY_SENDER", "REJECTED_BY_OPPONENT"].indexOf(
              call.status
            ) !== -1 && (
              <HistoryRow
                time={stringTimeFormat(call.createdAt)}
                text={LocalText.screens.callHistory.requestCanceled}
                variant={"red"}
              />
            )}
            {call.startedAt && (
              <HistoryRow
                time={stringTimeFormat(call.startedAt)}
                text={LocalText.screens.callHistory.callStarted}
              />
            )}
            {call.rejectedAt && (
              <HistoryRow
                time={stringTimeFormat(call.rejectedAt)}
                text={LocalText.screens.callHistory.callCanceled}
                variant={"red"}
              />
            )}
            {call.finishedAt && (
              <HistoryRow
                time={stringTimeFormat(call.finishedAt)}
                text={LocalText.screens.callHistory.callFinished}
              />
            )}
            {["EXPIRED"].indexOf(call.status) !== -1 && call.expiredAt && (
              <HistoryRow
                time={stringTimeFormat(call.expiredAt)}
                text={LocalText.screens.callHistory.requestExpired}
                variant={"red"}
              />
            )}
            {call.offerRejectMessage || call.offerCancelMessage ? (
              <React.Fragment>
                <Typography
                  text={LocalText.screens.callHistory.cancelReason}
                  variant={"section"}
                />
                <CallDataRow
                  text={
                    call.offerRejectMessage || call.offerCancelMessage || ""
                  }
                />
              </React.Fragment>
            ) : null}
            {call.senderReview || call.opponentReview ? (
              <React.Fragment>
                <Typography
                  text={LocalText.screens.callHistory.review}
                  variant={"section"}
                />
                {call.senderReview && (
                  <ReviewCard
                    review={call.senderReview}
                    containerStyle={styles.reviewCard}
                    onPress={() => console.log("onReview...")}
                  />
                )}
                {call.opponentReview && (
                  <ReviewCard
                    review={call.opponentReview}
                    containerStyle={styles.reviewCard}
                    onPress={() => console.log("onReview...")}
                  />
                )}
              </React.Fragment>
            ) : null}
            {cognitoUsername && checkReviewExpected(call, cognitoUsername) ? (
              <Button
                containerStyle={styles.reviewBtn}
                variant={"primary"}
                title={LocalText.screens.callHistory.openReview}
                onPress={() =>
                  navigation.navigate("Review", {
                    callId: route.params.callId,
                  })
                }
              />
            ) : null}

            {cognitoUsername && user && sections.length > 0 ? (
              <React.Fragment>
                <Typography
                  text={LocalText.screens.callHistory.chat}
                  variant={"section"}
                />
                <SectionList
                  inverted
                  scrollEnabled={false}
                  style={styles.scrollView}
                  sections={sections}
                  keyExtractor={(item) => item.id}
                  renderItem={({ item }) => (
                    <React.Fragment>
                      {item.ownerId !== cognitoUsername ? (
                        <LeftMessage
                          text={item.content?.text}
                          imageSource={
                            item.content?.image
                              ? { uri: item.content?.image }
                              : undefined
                          }
                        />
                      ) : null}
                      {item.ownerId === cognitoUsername ? (
                        <RightMessage
                          text={item.content?.text}
                          imageSource={
                            item.content?.image
                              ? { uri: item.content?.image }
                              : undefined
                          }
                          time={stringTimeFormat(item.createdAt || "")}
                        />
                      ) : null}
                    </React.Fragment>
                  )}
                />
              </React.Fragment>
            ) : null}
          </ScrollView>
          <BottomActions>
            <Button
              variant={"secondary"}
              icon={"chevron"}
              iconColor={colors.chokuRed}
              iconSize={12}
              title={LocalText.common.back}
              onPress={() => navigation.goBack()}
            />

            {cognitoUsername && call?.senderId === cognitoUsername ? (
              <Button
                variant={"primary"}
                icon={"send"}
                textStyle={{ fontSize: 13 }}
                title={LocalText.screens.callHistory.openReoffer}
                onPress={reofferHandler}
              />
            ) : (
              <Button
                variant={"primary"}
                icon={"send"}
                textStyle={{ fontSize: 13 }}
                title={LocalText.screens.callHistory.requestReoffer}
                onPress={requestReofferHandler}
              />
            )}
          </BottomActions>
        </React.Fragment>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: "column",
    backgroundColor: colors.white,
  },
  scrollView: {
    flex: 1,
  },
  user: {
    marginTop: 16,
    marginBottom: 8,
    marginHorizontal: 16,
  },
  info: {
    fontSize: 12,
    color: colors.chokuBlack,
    padding: 16,
  },
  infoBold: {
    fontWeight: "500",
    color: "#666666",
  },
  offerActionContainer: {
    flex: 1,
    marginLeft: 8,
  },
  price: {
    textAlign: "center",
    fontSize: 12,
    color: colors.chokuRed,
  },
  priceBold: {
    fontSize: 14,
    fontWeight: "bold",
  },
  reviewCard: {
    marginHorizontal: 16,
    marginBottom: 8,
  },
  reviewBtn: {
    margin: 16,
  },
});
